/**
 * Decides when the quiz player sends its watch progress to the server.
 *
 * The old code sent only when `Math.floor(currentTime) % 5 === 0`, checked on
 * ticks that come every 500 ms. A tick can skip that second, and the final
 * "completed" write could be lost, so the Cinefil badge stayed behind.
 */

/** Minimum time between two regular progress saves. */
export const VIDEO_SAVE_INTERVAL_MS = 5000;

/** Share of the video that counts as watched (same rule as the server). */
export const VIDEO_WATCHED_RATIO = 0.9;

export type VideoSaveState = {
  /** When the last save was sent (ms), null before the first one. */
  sentAt: number | null;
  /** Whole-second position of the last save. */
  position: number;
  /** Whether the last save told the server the video is completed. */
  completed: boolean;
};

export const initialVideoSaveState = (): VideoSaveState => ({
  sentAt: null,
  position: 0,
  completed: false,
});

export function isVideoWatched(position: number, duration: number): boolean {
  return duration > 0 && position >= duration * VIDEO_WATCHED_RATIO;
}

export function shouldSendVideoProgress(
  state: VideoSaveState,
  next: { now: number; position: number; completed: boolean; force?: boolean },
): boolean {
  // The first save, and the moment the video becomes completed, go out at once.
  if (state.sentAt === null) return true;
  if (next.completed && !state.completed) return true;

  const moved = Math.floor(next.position) !== state.position;
  if (!moved) return false;
  if (next.force) return true;
  return next.now - state.sentAt >= VIDEO_SAVE_INTERVAL_MS;
}

export function markVideoProgressSent(
  now: number,
  position: number,
  completed: boolean,
  previous: VideoSaveState,
): VideoSaveState {
  return {
    sentAt: now,
    position: Math.floor(position),
    completed: completed || previous.completed,
  };
}
